<template>
  <div class="mobile-page">
    <main class="mobile-shell">
      <section class="mobile-card">
        <FormHeader
          title="Your details"
          subtitle="Add your details to complete setup."
          :progress-value="100"
          progress-label="Step 2 of 2"
          :home-href="webURL"
          login-href="/login"
        />

        <FormCard>
          <FormLegend text="Personal details" />

          <div class="field-stack desktop-field-stack">
            <div class="desktop-two-col">
              <BaseInput
                id="fullName"
                v-model="form.fullName"
                label="Full name"
                type="text"
                placeholder="Your full name"
                autocomplete="name"
                :error-message="getFullNameError()"
                @blur="markTouched('fullName')"
              />

              <BaseInput
                id="idNumber"
                v-model="form.idNumber"
                label="ID number"
                type="text"
                placeholder="For example Sd32249-01"
                :hint="
                  !getIdNumberError()
                    ? 'Required for account verification.'
                    : ''
                "
                :error-message="getIdNumberError()"
                @blur="markTouched('idNumber')"
              />
            </div>

            <div class="desktop-two-col desktop-two-col--phone">
              <div class="field-group">
                <label for="phone">Mobile number</label>

                <div class="phone-row">
                  <div
                    class="country-prefix"
                    :class="{ 'is-error': !!getPhoneError() }"
                  >
                    <span>{{ ui.phoneMeta.flag }}</span>
                    <span>{{ ui.phoneMeta.dialCode }}</span>
                  </div>

                  <div
                    class="phone-input-wrap"
                    :class="{ 'is-error': !!getPhoneError() }"
                  >
                    <input
                      id="phone"
                      :value="form.phone"
                      type="tel"
                      inputmode="tel"
                      placeholder="7XXXXXXXX"
                      @input="form.phone = $event.target.value"
                      @blur="markTouched('phone')"
                    />
                  </div>
                </div>

                <small v-if="getPhoneError()" class="error-text">
                  {{ getPhoneError() }}
                </small>
              </div>

              <BaseInput
                id="birthYear"
                v-model="form.birthYear"
                label="Birth year"
                type="text"
                inputmode="numeric"
                placeholder="e.g. 1994"
                :error-message="getBirthYearError()"
                @blur="markTouched('birthYear')"
              />
            </div>
          </div>

          <div class="card-action-area">
            <button class="secondary-btn" type="button" @click="handleBack">
              Back
            </button>

            <div v-if="serverErrors.general" class="form-error-banner">
              {{ serverErrors.general }}
            </div>

            <div v-if="successMessage" class="form-success-banner">
              {{ successMessage }}
            </div>

            <PrimaryButton
              :disabled="submitting"
              type="button"
              @click="handleSubmit"
            >
              {{ submitting ? 'Completing...' : 'Complete registration' }}
            </PrimaryButton>

            <InlineNotice icon="🛡" variant="privacy">
              Your personal details are handled securely.
              <a :href="webURL + '/privacy-policy'">Read our Privacy Policy</a>.
            </InlineNotice>
          </div>
        </FormCard>
      </section>
    </main>
  </div>
</template>

<script>
import FormHeader from '@/components/registration/FormHeader.vue'
import FormCard from '@/components/registration/FormCard.vue'
import FormLegend from '@/components/registration/FormLegend.vue'
import BaseInput from '@/components/registration/BaseInput.vue'
import PrimaryButton from '@/components/registration/PrimaryButton.vue'
import InlineNotice from '@/components/registration/InlineNotice.vue'
import { useRegistrationForm } from '@/composables/useRegistrationForm'
import { useRouter } from 'vue-router'
import { reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { getLoadedRecaptchaToken } from '@/utils/recaptcha'
import { pushToDataLayer } from '@/utils/dataLayer'

export default {
  name: 'RegisterStep2',
  components: {
    FormHeader,
    FormCard,
    FormLegend,
    BaseInput,
    PrimaryButton,
    InlineNotice,
  },
  setup() {
    const router = useRouter()

    const store = useStore()

    const { form, ui, actions, validations } = useRegistrationForm()

    const serverErrors = reactive({
      email: '',
      title: '',
      category: '',
      target: '',
      duration: '',
      fullName: '',
      idNumber: '',
      phone: '',
      birthYear: '',
      general: '',
    })

    const submitting = ref(false)
    const successMessage = ref('')
    const webURL = process.env.VUE_APP_WEB_URL

    if (
      !form.email ||
      !form.title ||
      !form.category ||
      !form.target ||
      !form.duration
    ) {
      actions.goToStep(1)
      router.replace('/signup')
    }

    actions.goToStep(2)

    function handleBack() {
      actions.previousStep()
      router.push('/signup')
    }

    const validationUi = reactive({
      submitted: false,
      touched: {
        fullName: false,
        idNumber: false,
        phone: false,
        birthYear: false,
      },
    })

    function markTouched(field) {
      validationUi.touched[field] = true
    }

    function shouldShowError(field) {
      return validationUi.submitted || validationUi.touched[field]
    }

    function clearServerErrors() {
      Object.keys(serverErrors).forEach((key) => {
        serverErrors[key] = ''
      })
    }

    function getFullNameError() {
      return shouldShowError('fullName')
        ? validations.fullNameValidation.value.message
        : ''
    }

    function getIdNumberError() {
      return shouldShowError('idNumber')
        ? validations.idNumberValidation.value.message
        : ''
    }

    function getPhoneError() {
      if (serverErrors.phone) return serverErrors.phone
      return shouldShowError('phone')
        ? validations.phoneValidation.value.message
        : ''
    }

    function getBirthYearError() {
      return shouldShowError('birthYear')
        ? validations.birthYearValidation.value.message
        : ''
    }

    async function handleSubmit() {
      successMessage.value = ''
      clearServerErrors()

      form.fullName = form.fullName.trim()
      form.idNumber = form.idNumber.trim()
      form.phone = String(form.phone || '').replace(/\D/g, '')
      form.birthYear = String(form.birthYear || '').replace(/\D/g, '')

      validationUi.submitted = true

      if (!validations.step2IsValid.value) return

      submitting.value = true

      try {
        const attribution = actions.getAttributionPayload()
        const recaptchaToken = await getLoadedRecaptchaToken('register')

        const payload = {
          email: form.email.trim(),
          campaign_name: form.title.trim(),
          fundraiser_type: form.category,
          currency: form.currency,
          target_amount: Number(String(form.target).replace(/\D/g, '')),
          fundraiser_duration: Number(String(form.duration).replace(/\D/g, '')),
          referral_code: form.referralCode.trim(),
          account_name: form.fullName.trim(),
          identity_no: form.idNumber.trim(),
          phone: form.phone.slice(-9),
          phoneDialCode: ui.phoneMeta.dialCode,
          country_code: ui.phoneMeta.countryCode,
          date_of_birth: form.birthYear,
          attribution: attribution ? JSON.stringify(attribution) : null,
          gclid: attribution?.gclid || null,
          recaptcha_token: recaptchaToken,
        }

        pushToDataLayer({
          event: 'registration_submit',
          email: payload.email,
          gclid: attribution?.gclid || null,
        })

        const resp = await store.dispatch('userRegister', payload)

        if (resp?.error) {
          if (resp?.key && serverErrors[resp.key] !== undefined) {
            serverErrors[resp.key] = resp.error
            return
          }

          serverErrors.general =
            resp.error || 'Something went wrong. Please try again.'
          return
        }

        pushToDataLayer({
          event: 'enhanced_conversion',
          email: payload.email,
        })

        pushToDataLayer({
          event: 'conversion',
          email: payload.email,
        })

        pushToDataLayer({
          event: 'registration_success',
          email: payload.email,
          gclid: attribution?.gclid || null,
        })

        if (resp?.account) {
          successMessage.value =
            'Successfully registered. Redirecting you to your fundraiser...'

          setTimeout(() => {
            window.location.href = `${webURL}/fundraiser/${resp.account}`
          }, 1500)
        } else {
          serverErrors.general =
            'Registration succeeded, but we could not open your fundraiser automatically.'
        }
      } catch (error) {
        serverErrors.general =
          error?.message || 'Something went wrong. Please try again.'
      } finally {
        submitting.value = false
      }
    }

    return {
      form,
      ui,
      actions,
      validations,
      validationUi,
      serverErrors,
      successMessage,
      webURL,
      submitting,
      markTouched,
      getFullNameError,
      getIdNumberError,
      getPhoneError,
      getBirthYearError,
      handleBack,
      handleSubmit,
    }
  },
}
</script>

<style scoped>
.mobile-page {
  min-height: 100vh;
  padding: 16px;
  background: radial-gradient(
      circle at top left,
      rgba(136, 151, 57, 0.22),
      transparent 26%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(252, 196, 21, 0.16),
      transparent 24%
    ),
    linear-gradient(180deg, #fafaf7 0%, #fafaf7 100%);
  font-family: Calibri, 'Segoe UI', sans-serif;
  color: #26311d;
}

.mobile-shell {
  max-width: 430px;
  margin: 0 auto;
}

.mobile-card {
  background: transparent;
  padding: 2px 0 10px;
}

.field-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field-group {
  display: flex;
  flex-direction: column;
}

.field-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #36442c;
}

.phone-row {
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 10px;
}

.phone-input-wrap {
  border: 1.5px solid #b8c2aa;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
  overflow: hidden;
}

.phone-input-wrap:focus-within {
  border-color: #fcc415;
  box-shadow: 0 0 0 4px rgba(252, 196, 21, 0.18),
    0 9px 18px rgba(68, 85, 38, 0.08);
}

.phone-input-wrap input {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border: 0;
  border-radius: 0;
  background: #ffffff;
  font-size: 16px;
  color: #26311d;
  outline: none;
  box-shadow: none !important;
}

.phone-input-wrap input::placeholder {
  color: #8b9485;
}

.phone-input-wrap.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

.phone-input-wrap.is-error:focus-within {
  border-color: #cf7d7d;
  box-shadow: 0 0 0 4px rgba(207, 125, 125, 0.14),
    0 9px 18px rgba(207, 125, 125, 0.12);
}

.error-text {
  display: block;
  margin-top: 8px;
  color: #b05b5b !important;
  font-size: 12px;
  line-height: 1.5;
}

.card-action-area {
  margin-top: 20px;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.secondary-btn {
  width: 100%;
  min-height: 58px;
  border-radius: 18px;
  border: 1.5px solid #ccd3c3;
  background: #ffffff;
  color: #445526;
  font-size: 18px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.secondary-btn:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(136, 151, 57, 0.12),
    0 8px 16px rgba(68, 85, 38, 0.06);
}

.form-error-banner {
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid #cf7d7d;
  border-radius: 14px;
  background: rgba(207, 125, 125, 0.08);
  color: #b05b5b;
  font-size: 16px;
  line-height: 1.5;
}

.form-success-banner {
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid #7bb57b;
  border-radius: 14px;
  background: rgba(123, 181, 123, 0.08);
  color: #5a8c5a;
  font-size: 16px;
  line-height: 1.5;
}

.country-prefix {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1.5px solid #b8c2aa;
  background: #f7f9f2;
  font-size: 16px;
  color: #26311d;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
  display: flex;
  align-items: center;
  gap: 10px;
}

.country-prefix.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

@media (min-width: 768px) {
  .mobile-page {
    padding: 32px 24px;
  }

  .mobile-shell {
    max-width: 640px;
  }

  .mobile-card {
    padding: 8px 0 20px;
  }

  .desktop-field-stack {
    gap: 20px;
  }

  .desktop-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
  }

  .desktop-two-col--phone {
    grid-template-columns: 1.4fr 1fr;
  }

  .card-action-area {
    margin-top: 24px;
  }
}
</style>
